// BUILD-042 My HR Qualifications/Memberships local-screen addendum (2026-10-06 17:00).
// Authority: docs/superpowers/decisions/2026-10-06-build-042-my-hr-self-service-ux.md
// (Qualifications/Memberships addendum section).
//
// STILL NO BACKEND for any of this (same as the rest of MyHrSections.tsx) — everything here is
// local component state, resets on reload. What changes from the prior pass is the ACTION MODEL
// and STATUS vocabulary, per the Owner's concern: "Employees should contribute and maintain
// their information, but cannot erase submitted company evidence or silently change verified
// facts." Concretely:
//   - Work Experience / Education / Skills / Languages (self-reported, no verification concept):
//     Add / Edit / Archive / Restore. Archive hides from the current list but keeps full history
//     and is restorable — never a hard delete.
//   - Licenses/certifications: Submit qualification / Upload renewal / Request correction. An
//     employee can never mark their own submission "Verified" — that status only appears once
//     set (in this local demo, pre-seeded on the example record to show what it looks like).
//     Uploading a renewal moves a verified record back to "Awaiting verification" rather than
//     silently keeping the old verification against new/changed information.
//   - Memberships: Add / Update / Mark ended. Whether a membership is company-paid/job-required
//     is a company-controlled fact with no employee-facing control to set it — shown as a
//     read-only badge when present.
//   - Company-recorded/verified records: View / Request correction only — no Add, Edit, Archive.
//   - Attachments: Upload / View / Submit replacement — replacing never removes the prior
//     evidence; the old entry stays visible, marked superseded.
//
// (Oct 19 correction) The "Request correction" control that used to appear on a License or company-verified
// record has been REMOVED: it was a session-only demonstration, and the durable correction-request backend is
// deliberately limited to a fixed allow-list of GOVERNED profile facts (legal name, title, department, manager,
// hire date, employment status). Licences and company-recorded training are not on that list and there is no
// generic field write path, so no control that could never be saved is shown here. The original design note
// follows for history: it reused the exact same RequestCorrectionCard / HrRequestsContext mechanism built for Personal Details/Job/
// Report-to/Salary (see those files) — no new request mechanism, no new approvals engine, per
// this decision's explicit "reuse existing... do not build a general approvals engine" rule.
//
// All employee-facing trash-icon/bulk-select/checkbox delete controls from the PRIOR pass are
// removed here, replaced with explicit labeled actions (Archive / Mark ended / Request
// correction) whose meaning is stated inline, per the Owner's "make the buttons communicate
// those distinctions" instruction. An unsaved in-progress Add/Edit FORM can still be discarded
// via Cancel — that is not the same as deleting a saved record.
import { useState } from 'react';
import type { ReactNode } from 'react';
import { CollapsibleHelp } from './CollapsibleHelp';
import { useMyHrViewerRole, SIMULATED_HR_ADMIN_NAME } from './MyHrViewerRoleContext';
import { CardTitle } from './workspace/SectionHeading';
import { LocalDemoNote } from './workspace/LocalDemoNote';
// October 10 22:28 addendum: reuses the shared hrFormat.ts formatter instead of this file's own
// local date-time helper (removed) — avoids a parallel formatter where one already exists.
import { formatLocalDateTime as formatAt, formatLocalDate } from './hrFormat';

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-xs text-gray-500">{label}</label>
      {children}
    </div>
  );
}
const inputClass = 'border border-gray-300 rounded px-3 py-1.5 text-sm';

// ── Shared status/history vocabulary ────────────────────────────────────────────────────────────

type RecordStatus = 'self-reported' | 'awaiting-verification' | 'verified';

function StatusBadge({ status, verifiedByName, verifiedAt }: { status: RecordStatus; verifiedByName?: string; verifiedAt?: string }) {
  if (status === 'verified') {
    return (
      <span className="text-[11px] bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">
        {/* October 10 22:28 addendum: verifiedAt was previously rendered as the raw stored
            date-only string — now MM/DD/YYYY via the shared, day-shift-safe formatter. */}
        Verified{verifiedByName ? ` by ${verifiedByName}` : ''}{verifiedAt ? ` · ${formatLocalDate(verifiedAt)}` : ''}
      </span>
    );
  }
  if (status === 'awaiting-verification') {
    return <span className="text-[11px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">Awaiting verification</span>;
  }
  return <span className="text-[11px] bg-gray-100 text-gray-500 border border-gray-200 rounded-full px-2 py-0.5">Self-reported</span>;
}

/** Expiry is a SEPARATE fact from verification — a verified license can still be expired. Only
 *  rendered when the record actually carries an expiry date. October 10 22:28 addendum: display
 *  is now MM/DD/YYYY (shared formatter); the expired/not-expired COMPARISON itself still uses
 *  the raw stored date-only string unmodified — only the label text changed. */
function ExpiryBadge({ expiryDate }: { expiryDate?: string }) {
  if (!expiryDate) return null;
  const expired = new Date(expiryDate) < new Date();
  return (
    <span className={`text-[11px] rounded-full px-2 py-0.5 border ${expired ? 'bg-red-50 text-red-700 border-red-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
      {expired ? 'Expired' : 'Expires'} {formatLocalDate(expiryDate)}
    </span>
  );
}

interface HistoryEntry {
  id: string;
  action: string; // human sentence fragment, e.g. "Edited", "Archived", "Submitted", "Renewal uploaded"
  actorName: string;
  at: string; // ISO
  reason?: string;
  summary?: string; // short "what changed" line, e.g. prior value
}

/** Expandable per-record history — readable names/dates only, never a raw id as the primary
 *  label (ids may still appear in a record's OWN fields, e.g. a license number, which is
 *  legitimate record content, not an identity label). */
function HistoryToggle({ history }: { history: HistoryEntry[] }) {
  const [open, setOpen] = useState(false);
  if (history.length === 0) return null;
  return (
    <div className="mt-1">
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-xs text-[#4AABDE] hover:underline">
        {open ? 'Hide history' : `History (${history.length})`}
      </button>
      {open && (
        <ul className="mt-1.5 space-y-1 border-l-2 border-gray-100 pl-3">
          {history.map((h) => (
            <li key={h.id} className="text-xs text-gray-500">
              {h.action} by {h.actorName} on {formatAt(h.at)}
              {h.summary ? ` — ${h.summary}` : ''}
              {h.reason ? ` (${h.reason})` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Self-reported records: Work Experience / Education / Skills / Languages ───────────────────
// No verification concept at all for this group — "Self-reported" is the only status they ever
// carry. Add / Edit / Archive / Restore; archived items move to a separate collapsed list rather
// than disappearing, and can always be restored.

interface FieldDef {
  key: string; label: string; type: 'text' | 'date' | 'select' | 'textarea' | 'number';
  required?: boolean; options?: string[]; span2?: boolean;
}
interface ColumnDef { key: string; label: string }
interface SelfReportedRecord {
  id: string;
  fields: Record<string, string>;
  archived: boolean;
  history: HistoryEntry[];
}

function RecordForm({
  fields, draft, setDraft, idPrefix,
}: { fields: FieldDef[]; draft: Record<string, string>; setDraft: (d: Record<string, string>) => void; idPrefix: string }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {fields.map((f) => (
        <div key={f.key} className={f.span2 ? 'col-span-2' : ''}>
          <Field label={f.required ? `${f.label}*` : f.label} htmlFor={`${idPrefix}-${f.key}`}>
            {f.type === 'select' ? (
              <select id={`${idPrefix}-${f.key}`} className={inputClass} value={draft[f.key] ?? ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}>
                <option value="">-- Select --</option>
                {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.type === 'textarea' ? (
              <textarea id={`${idPrefix}-${f.key}`} className={inputClass} rows={2} value={draft[f.key] ?? ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />
            ) : (
              <input id={`${idPrefix}-${f.key}`} type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'} className={inputClass} value={draft[f.key] ?? ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />
            )}
          </Field>
        </div>
      ))}
    </div>
  );
}

function SelfReportedSection({
  category, addTitle, fields, columns, idPrefix, seed,
}: { category: string; addTitle: string; fields: FieldDef[]; columns: ColumnDef[]; idPrefix: string; seed: SelfReportedRecord[] }) {
  const [items, setItems] = useState<SelfReportedRecord[]>(seed);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [showArchived, setShowArchived] = useState(false);

  function emptyDraft(): Record<string, string> {
    return Object.fromEntries(fields.map((f) => [f.key, '']));
  }
  function openAdd() { setEditingId(null); setDraft(emptyDraft()); setFormOpen(true); }
  function openEdit(item: SelfReportedRecord) { setEditingId(item.id); setDraft({ ...item.fields }); setFormOpen(true); }
  function cancel() { setFormOpen(false); setEditingId(null); } // discarding an unsaved draft is fine — nothing saved was touched
  function canSave(): boolean { return fields.filter((f) => f.required).every((f) => (draft[f.key] ?? '').trim()); }

  function describeFields(fieldValues: Record<string, string>): string {
    return columns.map((c) => fieldValues[c.key]).filter(Boolean).join(' / ') || '(no summary)';
  }

  function save() {
    if (!canSave()) return;
    if (editingId) {
      setItems((prev) => prev.map((it) => {
        if (it.id !== editingId) return it;
        const priorSummary = describeFields(it.fields);
        return {
          ...it, fields: { ...draft },
          history: [{ id: `h-${Date.now()}`, action: 'Edited', actorName: 'You', at: new Date().toISOString(), summary: `Was: ${priorSummary}` }, ...it.history],
        };
      }));
    } else {
      setItems((prev) => [
        { id: `${idPrefix}-${Date.now()}`, fields: { ...draft }, archived: false, history: [{ id: `h-${Date.now()}`, action: 'Added', actorName: 'You', at: new Date().toISOString() }] },
        ...prev,
      ]);
    }
    setFormOpen(false);
    setEditingId(null);
  }
  function archive(id: string) {
    setItems((prev) => prev.map((it) => (it.id === id
      ? { ...it, archived: true, history: [{ id: `h-${Date.now()}`, action: 'Archived', actorName: 'You', at: new Date().toISOString() }, ...it.history] }
      : it)));
  }
  function restore(id: string) {
    setItems((prev) => prev.map((it) => (it.id === id
      ? { ...it, archived: false, history: [{ id: `h-${Date.now()}`, action: 'Restored', actorName: 'You', at: new Date().toISOString() }, ...it.history] }
      : it)));
  }

  const active = items.filter((i) => !i.archived);
  const archived = items.filter((i) => i.archived);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">{category}</h3>
        {!formOpen && (
          <button type="button" onClick={openAdd} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full">+ Add</button>
        )}
      </div>

      {formOpen && (
        <div className="mb-4 border border-gray-200 rounded-md p-4 bg-gray-50">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">{editingId ? `Edit ${category}` : addTitle}</h4>
          <RecordForm fields={fields} draft={draft} setDraft={setDraft} idPrefix={idPrefix} />
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-gray-400">* Required</span>
            <div className="flex gap-2">
              <button type="button" onClick={cancel} className="text-sm text-gray-600 border border-gray-300 rounded-full px-4 py-1 hover:bg-gray-100">Cancel</button>
              <button type="button" onClick={save} disabled={!canSave()} className="text-sm bg-[#6FA82C] hover:bg-[#5F9124] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full px-5 py-1">Save</button>
            </div>
          </div>
        </div>
      )}

      {active.length === 0 ? (
        <div className="text-sm text-gray-400 mb-2">No records yet.</div>
      ) : (
        <ul className="divide-y divide-gray-100 mb-2">
          {active.map((item) => (
            <li key={item.id} className="py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-gray-900">{describeFields(item.fields)}</div>
                  <div className="mt-1"><StatusBadge status="self-reported" /></div>
                  <HistoryToggle history={item.history} />
                </div>
                <div className="flex gap-3 flex-shrink-0 text-xs">
                  <button type="button" onClick={() => openEdit(item)} className="text-[#4AABDE] hover:underline">Edit</button>
                  <button type="button" onClick={() => archive(item.id)} className="text-gray-500 hover:underline" title="Removes this from your current list and keeps it in history — it is never deleted, and you can restore it later.">
                    Archive
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {archived.length > 0 && (
        <div className="mt-2">
          <button type="button" onClick={() => setShowArchived((v) => !v)} className="text-xs text-gray-500 hover:underline">
            {showArchived ? 'Hide' : 'Show'} archived ({archived.length})
          </button>
          {showArchived && (
            <ul className="divide-y divide-gray-100 mt-2">
              {archived.map((item) => (
                <li key={item.id} className="py-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-gray-500 line-through">{describeFields(item.fields)}</div>
                      <HistoryToggle history={item.history} />
                    </div>
                    <button type="button" onClick={() => restore(item.id)} className="text-xs text-[#4AABDE] hover:underline flex-shrink-0">Restore</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Licenses / certifications — verification-gated, no employee self-verify ────────────────────
interface LicenseRecord {
  id: string;
  fields: Record<string, string>; // licenseType, licenseNumber
  status: RecordStatus;
  verifiedByName?: string;
  verifiedAt?: string;
  expiryDate?: string;
  history: HistoryEntry[];
}

function LicenseSection({ employeeId, employeeName, seed }: { employeeId: string; employeeName: string; seed: LicenseRecord[] }) {
  const { role } = useMyHrViewerRole();
  const [items, setItems] = useState<LicenseRecord[]>(seed);
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState({ licenseType: '', licenseNumber: '', issuedDate: '', expiryDate: '' });
  const [renewingId, setRenewingId] = useState<string | null>(null);
  const [renewalExpiry, setRenewalExpiry] = useState('');

  // "Simulate each view for me" (2026-10-07): the ONLY new mutation this toggle introduces —
  // marking a submitted license Verified. This is deliberately gated to the simulated
  // HR-Administrator role only; an employee viewing their own record never gets this control,
  // matching the governing decision's "employees cannot verify their own submissions" rule even
  // in simulation. Still local-only state, same as every other action on this page.
  function verify(id: string) {
    if (role !== 'hrAdmin') return;
    setItems((prev) => prev.map((it) => (it.id === id
      ? {
          ...it, status: 'verified', verifiedByName: SIMULATED_HR_ADMIN_NAME, verifiedAt: new Date().toISOString().slice(0, 10),
          history: [{ id: `h-${Date.now()}`, action: 'Verified', actorName: SIMULATED_HR_ADMIN_NAME, at: new Date().toISOString() }, ...it.history],
        }
      : it)));
  }

  function openAdd() { setDraft({ licenseType: '', licenseNumber: '', issuedDate: '', expiryDate: '' }); setFormOpen(true); }
  function cancelAdd() { setFormOpen(false); } // discarding an unsaved submission draft, not a saved record

  function submit() {
    if (!draft.licenseType.trim()) return;
    setItems((prev) => [
      {
        id: `lic-${Date.now()}`,
        fields: { licenseType: draft.licenseType, licenseNumber: draft.licenseNumber },
        status: 'awaiting-verification',
        expiryDate: draft.expiryDate || undefined,
        history: [{ id: `h-${Date.now()}`, action: 'Submitted for verification', actorName: 'You', at: new Date().toISOString() }],
      },
      ...prev,
    ]);
    setFormOpen(false);
  }

  function startRenewal(item: LicenseRecord) {
    setRenewingId(item.id);
    setRenewalExpiry('');
  }
  function cancelRenewal() { setRenewingId(null); }
  function confirmRenewal(id: string) {
    if (!renewalExpiry) return;
    // Uploading a renewal moves the record BACK to "awaiting verification" rather than silently
    // keeping the old verification against new/changed information — decision: "Edits must not
    // silently carry an old verification forward to changed information."
    setItems((prev) => prev.map((it) => (it.id === id
      ? {
          ...it, status: 'awaiting-verification', expiryDate: renewalExpiry, verifiedByName: undefined, verifiedAt: undefined,
          history: [{ id: `h-${Date.now()}`, action: 'Renewal uploaded', actorName: 'You', at: new Date().toISOString(), summary: `New expiry proposed: ${renewalExpiry}` }, ...it.history],
        }
      : it)));
    setRenewingId(null);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">License</h3>
        {!formOpen && (
          <button type="button" onClick={openAdd} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full">+ Submit qualification</button>
        )}
      </div>

      {formOpen && (
        <div className="mb-4 border border-gray-200 rounded-md p-4 bg-gray-50">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Submit a qualification for verification</h4>
          <div className="grid grid-cols-2 gap-3">
            <Field label="License Type*" htmlFor="lic-type">
              <select id="lic-type" className={inputClass} value={draft.licenseType} onChange={(e) => setDraft((d) => ({ ...d, licenseType: e.target.value }))}>
                <option value="">-- Select --</option>
                <option>Driver's License</option><option>Professional License</option><option>Certification</option><option>Permit</option><option>Other</option>
              </select>
            </Field>
            <Field label="License Number" htmlFor="lic-num"><input id="lic-num" className={inputClass} value={draft.licenseNumber} onChange={(e) => setDraft((d) => ({ ...d, licenseNumber: e.target.value }))} /></Field>
            <Field label="Issued Date" htmlFor="lic-issued"><input id="lic-issued" type="date" className={inputClass} value={draft.issuedDate} onChange={(e) => setDraft((d) => ({ ...d, issuedDate: e.target.value }))} /></Field>
            <Field label="Expiry Date" htmlFor="lic-expiry"><input id="lic-expiry" type="date" className={inputClass} value={draft.expiryDate} onChange={(e) => setDraft((d) => ({ ...d, expiryDate: e.target.value }))} /></Field>
          </div>
          <p className="text-xs text-gray-400 mt-2">Local demonstration only — this is not sent anywhere real. It starts as "Awaiting verification"; in a real system, you would never be able to verify your own submission.</p>
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-gray-400">* Required</span>
            <div className="flex gap-2">
              <button type="button" onClick={cancelAdd} className="text-sm text-gray-600 border border-gray-300 rounded-full px-4 py-1 hover:bg-gray-100">Cancel</button>
              <button type="button" onClick={submit} disabled={!draft.licenseType.trim()} className="text-sm bg-[#6FA82C] hover:bg-[#5F9124] disabled:opacity-50 text-white rounded-full px-5 py-1">Submit</button>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-sm text-gray-400">No licenses or certifications on file yet.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {items.map((item) => (
            <li key={item.id} className="py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-gray-900">
                    {item.fields.licenseType}{item.fields.licenseNumber ? ` — ${item.fields.licenseNumber}` : ''}
                  </div>
                  <div className="mt-1 flex gap-1.5 flex-wrap">
                    <StatusBadge status={item.status} verifiedByName={item.verifiedByName} verifiedAt={item.verifiedAt} />
                    <ExpiryBadge expiryDate={item.expiryDate} />
                  </div>
                  <HistoryToggle history={item.history} />
                  {renewingId === item.id && (
                    <div className="mt-2 border border-gray-200 rounded-md p-3 bg-gray-50 max-w-sm">
                      <Field label="New expiry date" htmlFor={`renew-${item.id}`}>
                        <input id={`renew-${item.id}`} type="date" className={inputClass} value={renewalExpiry} onChange={(e) => setRenewalExpiry(e.target.value)} />
                      </Field>
                      <p className="text-xs text-gray-400 mt-1">This moves the record back to "Awaiting verification" — your current verified status is not carried forward to the renewed information.</p>
                      <div className="flex gap-2 mt-2">
                        <button type="button" onClick={() => confirmRenewal(item.id)} disabled={!renewalExpiry} className="text-xs bg-[#6FA82C] hover:bg-[#5F9124] disabled:opacity-50 text-white px-3 py-1.5 rounded">Upload renewal</button>
                        <button type="button" onClick={cancelRenewal} className="text-xs text-gray-500 px-3 py-1.5">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0 text-xs">
                  {role === 'hrAdmin' && item.status === 'awaiting-verification' && (
                    <button type="button" onClick={() => verify(item.id)} className="text-xs bg-[#6FA82C] hover:bg-[#5F9124] text-white px-3 py-1.5 rounded" title="Only HR Administration can mark a submission verified — an employee can never verify their own.">
                      Verify
                    </button>
                  )}
                  {role === 'employee' && item.status === 'verified' && renewingId !== item.id && (
                    <button type="button" onClick={() => startRenewal(item)} className="text-[#4AABDE] hover:underline">Upload renewal</button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Company-recorded / verified records — View / Request correction only ───────────────────────
// No Add, Edit, or Archive here at all — these are company-entered facts (e.g. required
// training completion) that an employee may view and request a correction on, never alter
// directly, and never hide by archiving.
interface CompanyVerifiedRecord {
  id: string;
  title: string;
  detail: string;
  verifiedByName: string;
  verifiedAt: string;
  expiryDate?: string;
}

function CompanyVerifiedSection({ employeeId, employeeName, seed }: { employeeId: string; employeeName: string; seed: CompanyVerifiedRecord[] }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-2">Company-Recorded Training &amp; Qualifications</h3>
      <p className="text-xs text-gray-400 mb-2">Entered and verified by HR Administration. You can view these and request a correction — you cannot edit, archive, or remove them.</p>
      {seed.length === 0 ? (
        <div className="text-sm text-gray-400">None on file yet.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {seed.map((item) => (
            <li key={item.id} className="py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-gray-900">{item.title}</div>
                  <div className="text-xs text-gray-500">{item.detail}</div>
                  <div className="mt-1 flex gap-1.5 flex-wrap">
                    <StatusBadge status="verified" verifiedByName={item.verifiedByName} verifiedAt={item.verifiedAt} />
                    <ExpiryBadge expiryDate={item.expiryDate} />
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Attachments — Upload / View / Submit replacement, never destructive ────────────────────────
interface AttachmentRecord {
  id: string;
  fileName: string;
  comment: string;
  uploadedAt: string;
  supersededBy?: string; // id of the attachment that replaced this one, if any
}

function AttachmentsSection() {
  const [items, setItems] = useState<AttachmentRecord[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [replacingId, setReplacingId] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { setError('That file is larger than 1MB. Please choose a smaller file.'); return; }
    setError(null);
    setFileName(file.name);
  }
  function openUpload() { setReplacingId(null); setFileName(''); setComment(''); setError(null); setFormOpen(true); }
  function openReplace(id: string) { setReplacingId(id); setFileName(''); setComment(''); setError(null); setFormOpen(true); }
  function cancel() { setFormOpen(false); setReplacingId(null); } // discards only the unsaved upload form

  function save() {
    if (!fileName) return;
    const newId = `att-${Date.now()}`;
    if (replacingId) {
      // Replacement never removes the old evidence — mark it superseded and keep it visible.
      setItems((prev) => [
        { id: newId, fileName, comment, uploadedAt: new Date().toISOString() },
        ...prev.map((it) => (it.id === replacingId ? { ...it, supersededBy: newId } : it)),
      ]);
    } else {
      setItems((prev) => [{ id: newId, fileName, comment, uploadedAt: new Date().toISOString() }, ...prev]);
    }
    setFileName(''); setComment(''); setFormOpen(false); setReplacingId(null);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">Attachments</h3>
        {!formOpen && (
          <button type="button" onClick={openUpload} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full">+ Upload</button>
        )}
      </div>

      {formOpen && (
        <div className="mb-4 border border-gray-200 rounded-md p-4 bg-gray-50">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">{replacingId ? 'Submit replacement' : 'Upload attachment'}</h4>
          {replacingId && (
            <p className="text-xs text-gray-500 mb-2">The file you're replacing stays on file and visible, marked superseded — it is never removed.</p>
          )}
          <Field label="Select File*" htmlFor="att-file">
            <div className="flex items-center gap-2">
              <label htmlFor="att-file" className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white cursor-pointer hover:bg-gray-50">Browse</label>
              <span className="text-sm text-gray-400">{fileName || 'No file selected'}</span>
              <input id="att-file" type="file" className="hidden" onChange={handleFileChange} />
            </div>
          </Field>
          <p className="text-xs text-gray-400 mt-1">Accepts up to 1MB.</p>
          {error && <p className="text-xs text-red-700 mt-1">{error}</p>}
          <div className="mt-3">
            <Field label="Comment" htmlFor="att-comment">
              <textarea id="att-comment" className={inputClass} rows={2} placeholder="Type comment here" value={comment} onChange={(e) => setComment(e.target.value)} />
            </Field>
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-gray-400">* Required</span>
            <div className="flex gap-2">
              <button type="button" onClick={cancel} className="text-sm text-gray-600 border border-gray-300 rounded-full px-4 py-1 hover:bg-gray-100">Cancel</button>
              <button type="button" onClick={save} disabled={!fileName} className="text-sm bg-[#6FA82C] hover:bg-[#5F9124] disabled:opacity-50 text-white rounded-full px-5 py-1">Save</button>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-sm text-gray-400">No attachments on file yet.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {items.map((item) => (
            <li key={item.id} className="py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-gray-900">{item.fileName}</div>
                  {item.comment && <div className="text-xs text-gray-500">{item.comment}</div>}
                  <div className="mt-1 flex gap-1.5 flex-wrap items-center">
                    {item.supersededBy ? (
                      <span className="text-[11px] bg-gray-100 text-gray-500 border border-gray-200 rounded-full px-2 py-0.5">Superseded by a newer version</span>
                    ) : (
                      <span className="text-[11px] bg-gray-50 text-gray-500 border border-gray-200 rounded-full px-2 py-0.5">On file · {formatAt(item.uploadedAt)}</span>
                    )}
                  </div>

                </div>
                <div className="flex gap-3 flex-shrink-0 text-xs">
                  <button type="button" onClick={() => setViewingId(viewingId === item.id ? null : item.id)} className="text-[#4AABDE] hover:underline">View</button>
                  {!item.supersededBy && (
                    <button type="button" onClick={() => openReplace(item.id)} className="text-gray-600 hover:underline" title="Adds a new version. The current file stays on file, marked superseded — nothing already submitted is removed.">
                      Submit replacement
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Memberships — Add / Update / Mark ended ─────────────────────────────────────────────────────
interface MembershipRecord {
  id: string;
  organization: string;
  memberSince: string;
  endedAt?: string;
  companyControlled?: boolean; // view-only badge; no employee action can set/clear this
  history: HistoryEntry[];
}

export function MembershipsPage({ employeeId, employeeName }: { employeeId: string; employeeName: string }) {
  const [items, setItems] = useState<MembershipRecord[]>([
    {
      id: 'mem-seed-1', organization: 'Society for Human Resource Management (synthetic)', memberSince: '2023-01-15',
      companyControlled: true,
      history: [{ id: 'h-seed', action: 'Added', actorName: 'You', at: '2023-01-15T09:00:00Z' }],
    },
  ]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ organization: '', memberSince: '' });

  function openAdd() { setEditingId(null); setDraft({ organization: '', memberSince: '' }); setFormOpen(true); }
  function openEdit(item: MembershipRecord) { setEditingId(item.id); setDraft({ organization: item.organization, memberSince: item.memberSince }); setFormOpen(true); }
  function cancel() { setFormOpen(false); setEditingId(null); }

  function save() {
    if (!draft.organization.trim()) return;
    if (editingId) {
      setItems((prev) => prev.map((it) => (it.id === editingId
        ? { ...it, organization: draft.organization, memberSince: draft.memberSince, history: [{ id: `h-${Date.now()}`, action: 'Updated', actorName: 'You', at: new Date().toISOString() }, ...it.history] }
        : it)));
    } else {
      setItems((prev) => [
        { id: `mem-${Date.now()}`, organization: draft.organization, memberSince: draft.memberSince, history: [{ id: `h-${Date.now()}`, action: 'Added', actorName: 'You', at: new Date().toISOString() }] },
        ...prev,
      ]);
    }
    setFormOpen(false); setEditingId(null);
  }
  function markEnded(id: string) {
    setItems((prev) => prev.map((it) => (it.id === id
      ? { ...it, endedAt: new Date().toISOString().slice(0, 10), history: [{ id: `h-${Date.now()}`, action: 'Marked ended', actorName: 'You', at: new Date().toISOString() }, ...it.history] }
      : it)));
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      <CardTitle
        action={!formOpen ? (
          <button type="button" onClick={openAdd} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full">+ Add</button>
        ) : undefined}
      >
        Memberships
      </CardTitle>
      <LocalDemoNote text="Local demonstration only. Nothing on this page is saved to a real record — reloading resets every entry below, including the seeded example." />

      {formOpen && (
        <div className="mb-4 border border-gray-200 rounded-md p-4 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{editingId ? 'Update membership' : 'Add membership'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Organization*" htmlFor="mem-org"><input id="mem-org" className={inputClass} value={draft.organization} onChange={(e) => setDraft((d) => ({ ...d, organization: e.target.value }))} /></Field>
            <Field label="Member since" htmlFor="mem-since"><input id="mem-since" type="date" className={inputClass} value={draft.memberSince} onChange={(e) => setDraft((d) => ({ ...d, memberSince: e.target.value }))} /></Field>
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-gray-400">* Required</span>
            <div className="flex gap-2">
              <button type="button" onClick={cancel} className="text-sm text-gray-600 border border-gray-300 rounded-full px-4 py-1 hover:bg-gray-100">Cancel</button>
              <button type="button" onClick={save} disabled={!draft.organization.trim()} className="text-sm bg-[#6FA82C] hover:bg-[#5F9124] disabled:opacity-50 text-white rounded-full px-5 py-1">Save</button>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-sm text-gray-400">No memberships added yet.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {items.map((m) => (
            <li key={m.id} className="py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {/* October 10 22:28 addendum: memberSince/endedAt were previously rendered as
                      the raw stored date-only string — now MM/DD/YYYY via the shared formatter. */}
                  <div className="text-sm text-gray-900">
                    {m.organization}
                    {m.endedAt && <span className="text-gray-400"> — ended {formatLocalDate(m.endedAt)}</span>}
                  </div>
                  <div className="text-xs text-gray-500">Member since {m.memberSince ? formatLocalDate(m.memberSince) : '—'}</div>
                  <div className="mt-1 flex gap-1.5 flex-wrap">
                    {m.companyControlled && (
                      <span className="text-[11px] bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5" title="Company-paid/job-required status is set by HR Administration — you cannot change this flag.">
                        Company-paid / job-required
                      </span>
                    )}
                    {m.endedAt && <span className="text-[11px] bg-gray-100 text-gray-500 border border-gray-200 rounded-full px-2 py-0.5">Ended</span>}
                  </div>
                  <HistoryToggle history={m.history} />
                </div>
                {!m.endedAt && (
                  <div className="flex gap-3 flex-shrink-0 text-xs">
                    <button type="button" onClick={() => openEdit(m)} className="text-[#4AABDE] hover:underline">Update</button>
                    <button type="button" onClick={() => markEnded(m.id)} className="text-gray-500 hover:underline" title="Marks this membership as no longer active. It stays on file and in history — it is never deleted.">
                      Mark ended
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <CollapsibleHelp>
        <p>You can add, update, and mark your own memberships ended. Whether a membership is company-paid or job-required is set by HR Administration — there is no control here to change that.</p>
      </CollapsibleHelp>
    </div>
  );
}

// ── Assembled Qualifications page ───────────────────────────────────────────────────────────────
export function QualificationsPage({ employeeId, employeeName }: { employeeId: string; employeeName: string }) {
  return (
    <div className="space-y-3">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <CardTitle>Qualifications</CardTitle>
        <LocalDemoNote text="Local demonstration only. Nothing on this page is saved to a real record — reloading resets every entry below, including the seeded examples." />
        <div className="space-y-6 mt-2">
          <SelfReportedSection
            category="Work Experience" addTitle="Add Work Experience" idPrefix="we"
            fields={[
              { key: 'company', label: 'Company', type: 'text', required: true },
              { key: 'jobTitle', label: 'Job Title', type: 'text', required: true },
              { key: 'from', label: 'From', type: 'date' },
              { key: 'to', label: 'To', type: 'date' },
              { key: 'comment', label: 'Comment', type: 'textarea', span2: true },
            ]}
            columns={[{ key: 'company', label: 'Company' }, { key: 'jobTitle', label: 'Job Title' }]}
            seed={[{
              id: 'we-seed-1',
              fields: { company: 'TCS (synthetic)', jobTitle: 'Software Tester', from: '2020-02-09', to: '2023-10-07', comment: 'Hi hello' },
              archived: false,
              history: [{ id: 'h-seed', action: 'Added', actorName: 'You', at: '2023-10-08T10:00:00Z' }],
            }]}
          />
          <div className="pt-6 border-t border-gray-100">
            <SelfReportedSection
              category="Education" addTitle="Add Education" idPrefix="edu"
              fields={[
                { key: 'level', label: 'Level', type: 'select', required: true, options: ["High School", 'Associate Degree', "Bachelor's Degree", "Master's Degree", 'Doctorate', 'Other'] },
                { key: 'institute', label: 'Institute', type: 'text' },
                { key: 'major', label: 'Major/Specialization', type: 'text' },
                { key: 'year', label: 'Year', type: 'text' },
                { key: 'gpa', label: 'GPA/Score', type: 'text' },
                { key: 'startDate', label: 'Start Date', type: 'date' },
                { key: 'endDate', label: 'End Date', type: 'date' },
              ]}
              columns={[{ key: 'level', label: 'Level' }, { key: 'year', label: 'Year' }]}
              seed={[{
                id: 'edu-seed-1',
                fields: { level: "Bachelor's Degree", institute: '', major: '', year: '', gpa: '', startDate: '', endDate: '' },
                archived: false,
                history: [{ id: 'h-seed', action: 'Added', actorName: 'You', at: '2023-01-01T10:00:00Z' }],
              }]}
            />
          </div>
          <div className="pt-6 border-t border-gray-100">
            <SelfReportedSection
              category="Skills" addTitle="Add Skill" idPrefix="skill"
              fields={[
                { key: 'skill', label: 'Skill', type: 'select', required: true, options: ['Communication', 'Leadership', 'Project Management', 'Customer Service', 'Sales', 'Technical Writing', 'Electrical', 'Plumbing', 'Carpentry', 'Other'] },
                { key: 'yearsExperience', label: 'Years of Experience', type: 'number' },
                { key: 'comments', label: 'Comments', type: 'textarea', span2: true },
              ]}
              columns={[{ key: 'skill', label: 'Skill' }, { key: 'yearsExperience', label: 'Years of Experience' }]}
              seed={[]}
            />
          </div>
          <div className="pt-6 border-t border-gray-100">
            <SelfReportedSection
              category="Languages" addTitle="Add Language" idPrefix="lang"
              fields={[
                { key: 'language', label: 'Language', type: 'select', required: true, options: ['English', 'Spanish', 'French', 'German', 'Mandarin', 'Other'] },
                { key: 'fluency', label: 'Fluency', type: 'select', required: true, options: ['Speaking', 'Reading', 'Writing', 'Speaking & Writing', 'Reading & Writing', 'All'] },
                { key: 'competency', label: 'Competency', type: 'select', required: true, options: ['Mother Tongue', 'Fluent', 'Proficient', 'Intermediate', 'Basic'] },
                { key: 'comments', label: 'Comments', type: 'textarea', span2: true },
              ]}
              columns={[{ key: 'language', label: 'Language' }, { key: 'fluency', label: 'Fluency' }, { key: 'competency', label: 'Competency' }]}
              seed={[{
                id: 'lang-seed-1',
                fields: { language: 'English', fluency: 'Speaking', competency: 'Mother Tongue', comments: '' },
                archived: false,
                history: [{ id: 'h-seed', action: 'Added', actorName: 'You', at: '2023-01-01T10:00:00Z' }],
              }]}
            />
          </div>
          <div className="pt-6 border-t border-gray-100">
            <LicenseSection
              employeeId={employeeId} employeeName={employeeName}
              seed={[
                {
                  id: 'lic-seed-verified',
                  fields: { licenseType: "Driver's License", licenseNumber: 'DL-04821' },
                  status: 'verified', verifiedByName: 'Dana HR Admin (synthetic)', verifiedAt: '2024-02-10',
                  expiryDate: '2027-06-01',
                  history: [
                    { id: 'h-seed-1', action: 'Submitted for verification', actorName: 'You', at: '2024-02-01T09:00:00Z' },
                    { id: 'h-seed-2', action: 'Verified', actorName: 'Dana HR Admin (synthetic)', at: '2024-02-10T14:00:00Z' },
                  ],
                },
                {
                  id: 'lic-seed-expired',
                  fields: { licenseType: 'Professional License', licenseNumber: 'PL-9931' },
                  status: 'verified', verifiedByName: 'Dana HR Admin (synthetic)', verifiedAt: '2022-03-01',
                  expiryDate: '2025-03-01',
                  history: [
                    { id: 'h-seed-3', action: 'Submitted for verification', actorName: 'You', at: '2022-02-15T09:00:00Z' },
                    { id: 'h-seed-4', action: 'Verified', actorName: 'Dana HR Admin (synthetic)', at: '2022-03-01T14:00:00Z' },
                  ],
                },
              ]}
            />
          </div>
          <div className="pt-6 border-t border-gray-100">
            <CompanyVerifiedSection
              employeeId={employeeId} employeeName={employeeName}
              seed={[{
                id: 'cv-seed-1', title: 'Forklift Safety Certification', detail: 'Required annual safety training — completed in person',
                verifiedByName: 'Dana HR Admin (synthetic)', verifiedAt: '2026-01-10', expiryDate: '2027-01-10',
              }]}
            />
          </div>
          <div className="pt-6 border-t border-gray-100">
            <AttachmentsSection />
          </div>
        </div>
      </div>

      <CollapsibleHelp>
        <p>You can add, edit, and archive your own Work Experience, Education, Skills, and Languages — archiving removes an entry from your current list but keeps it in history, and you can restore it.</p>
        <p>Licenses and company-recorded training are different: you can submit a qualification or upload a renewal, but only HR Administration can mark something Verified. Company-recorded records can only be viewed and corrected by request — never edited or archived directly.</p>
      </CollapsibleHelp>
    </div>
  );
}
